import { DocumentNode, useApolloClient } from '@apollo/client';
import { EntityBase } from '@provision/entity-types';
import { useCallback, useEffect, useRef } from 'react';
import { isDeepEqual } from 'remeda';
import { asyncScheduler, Subject } from 'rxjs';
import { groupBy, mergeMap, throttleTime } from 'rxjs/operators';
import { ttlLink } from 'src/graphql/setup/config';
import { Records } from 'src/graphql/types';
import { useGlobalStore } from 'src/store';
import { removeNotDefinedProperties } from 'src/utils/objects';

interface InvalidationRequest {
  operationName: string;
  variables: Record<string, unknown>;
  query: DocumentNode;
  hashKey?: string;
}

export type TriggerInvalidate = (params: InvalidationRequest) => void;

const DEFAULT_THROTTLE_SIZE = 2000;

export function useInvalidateAndRefetch(
  throttleSize = DEFAULT_THROTTLE_SIZE,
): TriggerInvalidate {
  const selectedPartnerId = useGlobalStore.use.selectedPartnerId() ?? '*';
  const client = useApolloClient();
  const refetch$ = useRef(new Subject<InvalidationRequest>());
  const pendingRef = useRef(new Map<string, InvalidationRequest>());
  const isVisibleRef = useRef(document.visibilityState === 'visible');

  // flush pending when tab becomes visible
  useEffect(() => {
    const onVisibilityChange = () => {
      const isVisible = document.visibilityState === 'visible';
      if (isVisible && !isVisibleRef.current) {
        // became visible, processing
        for (const req of pendingRef.current.values()) {
          refetch$.current.next(req);
        }
        pendingRef.current.clear();
      }
      isVisibleRef.current = isVisible;
    };

    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, []);

  useEffect(() => {
    const sub = refetch$.current
      .pipe(
        groupBy((req) => (req.hashKey ? req.hashKey : req.operationName)),
        mergeMap((group$) =>
          group$.pipe(
            throttleTime(throttleSize, asyncScheduler, {
              leading: false,
              trailing: true,
            }),
          ),
        ),
      )
      .subscribe(({ query, variables: rawVariables, operationName }) => {
        const { __partnerId, ...variables } = rawVariables;

        // each partner uses its own cache instance, so identical queries are stored separately per partner.
        // Without filtering, we’d end up refetching the same query across all partner caches.
        // Instead, only invalidate and refetch for the active partner; other partners’ queries will refresh when their TTL expires.
        if (String(selectedPartnerId) !== String(__partnerId)) {
          return;
        }

        // grab only the currently active queries
        const activeQs = client.getObservableQueries([operationName]);

        // check if _any_ of them match this document + variables
        const shouldRefetch = Array.from(activeQs.values()).some((obsQ) =>
          isDeepEqual(
            removeNotDefinedProperties(obsQ.options.variables || {}),
            removeNotDefinedProperties(variables || {}),
          ),
        );

        if (!shouldRefetch) {
          return;
        }

        void (async () => {
          // get a fresh first slice without touching the cache
          const { data: freshData } = await client.query<Record<string, Records<EntityBase>>>({
            query,
            variables: { ...variables, offset: 0 },
            fetchPolicy: 'no-cache',
          });

          // patch the existing query result in cache
          client.cache.updateQuery(
            { query, variables: { ...variables, offset: 0 } },
            (prev: Record<string, Records<EntityBase>>) =>
              prev ? mergeFirstPageAndPersistPagination(prev, freshData) : prev,
          );
        })();
      });

    return () => sub.unsubscribe();
  }, [client, throttleSize, selectedPartnerId]);

  // trigger: always invalidate TTL, then either emit or stash
  return useCallback<TriggerInvalidate>(({ operationName, variables, query, hashKey }) => {
    const req: InvalidationRequest = { operationName, variables, query, hashKey };

    // immediate cache‐TTL invalidation
    ttlLink.invalidateQuery(operationName, variables);

    if (isVisibleRef.current) {
      // fire off refetch through the subject (goes through throttle)
      refetch$.current.next(req);
    } else {
      // stash for when the tab comes back
      pendingRef.current.set(hashKey || operationName, req);
    }
  }, []);
}

function mergeFirstPageAndPersistPagination<T extends Record<string, Records<EntityBase>>>(
  oldData: T,
  freshData: T,
) {
  const key = Object.keys(oldData)[0] as keyof T;

  const newPage = freshData[key]?.records ?? [];
  const prev = oldData[key]?.records ?? [];

  // Drop duplicates that are already in the fresh page.
  const rest = prev.filter((rec) => !newPage.some((n) => n === rec || n?.id === rec?.id));

  return {
    ...oldData,
    [key]: {
      ...oldData[key],
      ...freshData[key],
      records: [...newPage, ...rest],
    },
  };
}
